/**
 * OrbitCamera - A class that provides a way to create an
 * interactive view in a 3D environment where users can peak
 * around a focal point by dragging their mouse.
 *
 * This class is intended to be used in p5.js projects.
 *
 * @param {number} [maxRotationAngle=degToRad(10)] - The maximum angle by which the camera can rotate (default is 10 degrees in radians)
 * @param {number} [damping=0.05] - The damping factor for the camera rotation
 * @param {number} w - Canvas width
 * @param {number} h - Canvas height
 * @param {number} density - Pixel density of the canvas
 */
export class OrbitCamera {
  constructor(maxRotationAngle = this.degToRad(10), damping = 0.05, w, h, density = 1) {
    this.maxAngle = maxRotationAngle; // the maximum angle by which the camera can rotate
    this.damping = damping; // the damping factor for the camera rotation
    this.init(w, h, density);
  }

  init(w, h, density = 1) {
    this.containerWidth = w;
    this.containerHeight = h;
    this.pixelDensity = density;
    this.initMouse = createVector(0, 0);
    this.rotX = 0;
    this.rotY = 0;
    // Calculate maxDragDistance based on canvas size and pixel density
    // Use the smaller dimension as the basis to ensure consistent behavior
    const minDimension = min(w, h);
    this.maxDragDistance = (minDimension * this.pixelDensity) / 10; // Scale relative to canvas size
    this.isReturningToOrigin = false; // set to true when the camera is currently returning to its original position
    this.camDist = this.calculateCamDist();
    this.isMouseDown = false;
    this.targetRotX = 0;
    this.targetRotY = 0;
    this.minRot = PI * 0.001; // the minimum rotation angle before the camera is considered to be at its origin
  }

  // Set initial mouse position and initiate dragging state
  mousePressed(mx, my) {
    this.isActive = true;
    this.initMouse.set(mx, my);
    this.isMouseDown = true;
  }

  // Update camera orientation based on current and initial mouse position
  mouseDragged(mx, my) {
    let dragVector = createVector(mx - this.initMouse.x, my - this.initMouse.y);

    // Ensure the drag effect is limited to a predefined max distance
    if (dragVector.magSq() > this.maxDragDistance * this.maxDragDistance) {
      dragVector.setMag(this.maxDragDistance);
    }

    this.updateRotation(dragVector);
  }

  mouseReleased() {
    this.isReturningToOrigin = true;
    this.targetRotX = 0; // Set target rotation to 0 for rubberbanding effect
    this.targetRotY = 0; // Set target rotation to 0 for rubberbanding effect
    this.isMouseDown = false;
  }

  updateRotation(dragVector) {
    this.targetRotY = map(
      -dragVector.x,
      -this.maxDragDistance,
      this.maxDragDistance,
      -this.maxAngle,
      this.maxAngle
    );
    this.targetRotX = map(
      -dragVector.y,
      -this.maxDragDistance,
      this.maxDragDistance,
      -this.maxAngle,
      this.maxAngle
    );
  }

  begin() {
    const isCloseToOrigin =
      abs(this.rotX) < this.minRot && abs(this.rotY) < this.minRot;

    if (this.isReturningToOrigin && !this.isMouseDown && isCloseToOrigin) {
      this.isReturningToOrigin = false;
    }

    // Apply lerp to both rotX and rotY to make them "chase" the target rotation
    this.rotX = lerp(this.rotX, this.targetRotX, this.damping);
    this.rotY = lerp(this.rotY, this.targetRotY, this.damping);

    let camX = this.camDist * sin(this.rotY) * cos(this.rotX);
    let camY = this.camDist * sin(this.rotX);
    let camZ = this.camDist * cos(this.rotY) * cos(this.rotX);

    camera(camX, camY, camZ, 0, 0, 0, 0, 1, 0);
  }

  end() {
    this.resetCam();
  }

  getNormalizedRotation() {
    return createVector(
      this.rotX / (this.maxAngle * 2),
      this.rotY / (this.maxAngle * 2)
    );
  }

  resetCam() {
    camera(0, 0, this.calculateCamDist(), 0, 0, 0, 0, 1, 0);
  }

  calculateCamDist() {
    let minDim = min(this.containerWidth, this.containerHeight);
    return minDim / 2 / tan(this.radiansToActiveMode(PI / 6));
  }

  radiansToActiveMode(a) {
    return angleMode() === RADIANS ? a : this.degToRad(a);
  }

  degToRad(deg) {
    return deg * (PI / 180);
  }

  resized(w, h, density) {
    this.init(w, h, density);
  }
}
